import React, { FC, ReactNode, useEffect } from 'react';

import PropTypes from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';

import * as analytics from '@adsk/acs-web-analytics';
import { TOOL_IDS, ToolIdType } from '@adsk/alloy-provider';
import { report, REPORT_TYPES } from '@adsk/exo-skeleton-api';
import { RemoteProvider } from '@adsk/exo-skeleton-react';

import LazyComponent, { LazyComponentProps } from './LazyComponent';
import { ANALYTICS_TYPES } from '../../analytics';
import { EXO_SKELETON_SCOPE_NAME } from '../../consts';
import { useRemote } from '../../providers/RemotesProvider';
import {
  REPORT_EVENT_TYPES,
  removeCurrentToolScopeFromContext,
  setCurrentToolScopeToContext,
} from '../../utils/monitoring';
import AppContentError from '../AppContentError/AppContentError';

export type RemoteComponentProps = Omit<LazyComponentProps, 'url'> & {
  toolId?: ToolIdType | 'demo-app' | '';
  renderError?: () => ReactNode;
};

const RemoteComponent: FC<RemoteComponentProps> = ({
  renderError = () => <AppContentError />,
  toolId,
  children,
  ...props
}) => {
  const { scope, module } = props;

  const remote = useRemote(scope);

  if (!remote) {
    throw new Error(`Remote with scope "${scope}" not found`);
  }

  useEffect(() => {
    if (toolId) {
      setCurrentToolScopeToContext(scope);
    }

    return () => {
      if (toolId) {
        removeCurrentToolScopeFromContext();
      }
    };
  }, [scope, toolId]);

  return (
    <ErrorBoundary
      fallbackRender={() => renderError()}
      resetKeys={[module, scope, children, toolId]}
      onError={(err) => {
        analytics.track(ANALYTICS_TYPES.EXO_TOOL_INTERNAL_ERROR, {
          navigate_url: window.location.href,
          remote_entry_url: new URL(
            remote.url,
            window.location.href,
          ).toString(),
          requested_tool: scope,
        });

        return report({
          scope: EXO_SKELETON_SCOPE_NAME,
          type: REPORT_TYPES.error,
          value: err,
          context: {},
          UNSAFE__globalContext: {
            type: REPORT_EVENT_TYPES.REMOTE_MODULE_ERROR,
            scope,
            module,
          },
        });
      }}
    >
      <RemoteProvider scope={scope} moduleName={module}>
        <LazyComponent url={remote.url} {...props}>
          {children}
        </LazyComponent>
      </RemoteProvider>
    </ErrorBoundary>
  );
};

RemoteComponent.propTypes = {
  module: PropTypes.string.isRequired,
  scope: PropTypes.string.isRequired,
  renderLoader: PropTypes.func,
  children: PropTypes.node,
  renderError: PropTypes.func,
  toolId: PropTypes.oneOf([...Object.values(TOOL_IDS), 'demo-app', '']),
};

export default RemoteComponent;
